const AUTH_TOKEN_KEY = "AUTH_TOKEN";
const AUTH_PROFILE_KEY = "BOOST_AUTH_PROFILE";
const RETIRED_AUTH_STORAGE_KEYS = [
  "token",
  "email",
  "user",
  "name",
  "className",
  "role",
  "avatar",
  "persist:root",
];

const safeParseJson = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const base64UrlDecode = (value) => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padLength = (4 - (base64.length % 4)) % 4;
  const padded = base64 + "=".repeat(padLength);
  return atob(padded);
};

const readLocal = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch (error) {
    return "";
  }
};

const writeLocal = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    // no-op
  }
};

const removeLocal = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // no-op
  }
};

const readProfile = () => {
  const raw = readLocal(AUTH_PROFILE_KEY);
  const parsed = safeParseJson(raw);
  return parsed && typeof parsed === "object" ? parsed : {};
};

const writeProfile = (profile = {}) => {
  writeLocal(AUTH_PROFILE_KEY, JSON.stringify(profile));
};

const removeRetiredKeys = () => {
  RETIRED_AUTH_STORAGE_KEYS.forEach((key) => removeLocal(key));
};

const workflowOwner = (profile = {}) => {
  const user = typeof profile.user === 'string' ? safeParseJson(profile.user) || {} : profile.user || {};
  const clean = value => String(value || '').trim();
  return JSON.stringify([
    clean(profile.role || user.role).toUpperCase(),
    clean(user.schoolId || user.school_id || user.schoolCode || profile.selectedSchoolCode),
    clean(user.userId || user.ID || user.email || user.loginId),
    clean(user.studentId || user.student_id || user.erpStudentId || user.erpId),
  ]);
};

const clearWorkflowStorage = () => {
  try {
    sessionStorage.removeItem('BOOSTER_PRACTICE_SESSION');
    sessionStorage.removeItem('BOOST_DEMO_ATTEMPTS');
  } catch (_error) { /* Storage may be unavailable in a restricted WebView. */ }
};

export const getStoredToken = () => {
  return readLocal(AUTH_TOKEN_KEY).trim();
};

export const getStoredRole = () => {
  const profile = readProfile();
  return String(profile?.role || "");
};

export const setAuthSession = (session = {}) => {
  const previousProfile = readProfile();
  const token = String(session.token || "").trim();
  const profile = {
    version: 3,
    user: session.user || "",
    name: session.name || "",
    className: session.className || "",
    role: session.role || "",
    avatar: session.avatar || "",
    demoMode: Boolean(session.demoMode),
    scoreEnabled: session.scoreEnabled !== false,
    selectedSchoolCode: String(session.selectedSchoolCode || ""),
    selectionRequired: Boolean(session.selectionRequired),
    memberships: Array.isArray(session.memberships) ? session.memberships : [],
    updatedAt: Date.now(),
  };

  if (workflowOwner(previousProfile) !== workflowOwner(profile)) clearWorkflowStorage();

  if (token) {
    writeLocal(AUTH_TOKEN_KEY, token);
  } else {
    removeLocal(AUTH_TOKEN_KEY);
  }
  writeProfile(profile);
  removeRetiredKeys();
  return { token, ...profile };
};

export const getStoredAuthProfile = () => readProfile();

export const isDemoModeSession = () => {
  const profile = readProfile();
  return Boolean(profile?.demoMode) || profile?.scoreEnabled === false;
};

export const clearAuthStorage = () => {
  removeLocal(AUTH_TOKEN_KEY);
  removeLocal(AUTH_PROFILE_KEY);
  removeRetiredKeys();
  clearWorkflowStorage();
};

export const getJwtPayload = (token) => {
  if (!token) return null;
  const parts = String(token).split(".");
  if (parts.length !== 3) return null;
  try {
    return JSON.parse(base64UrlDecode(parts[1]));
  } catch (error) {
    return null;
  }
};

export const isTokenExpired = (token, skewMs = 30000) => {
  const payload = getJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return false;
  const expiresAt = payload.exp * 1000;
  return Date.now() >= expiresAt - skewMs;
};

export { AUTH_TOKEN_KEY, AUTH_PROFILE_KEY };
